// Settings (settings prototype): the company's rules and defaults, the roles matrix with who holds each
// role, and the roles each person holds with what they add up to (BL-137: permissions are role-based,
// a person holds several), with the owner's limits (decision 18). Every value comes from and goes to
// the settings registry; the server checks every change again.

import { useCallback, useEffect, useRef, useState } from 'react';
import { SignOutEverywhere } from './SignOutEverywhere.tsx';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { Header, INVENTORY_PERMS, PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { holdsAny } from '../shared/may.ts';
import { useDesign } from '../blueprint/design.ts';
import { Moved } from '../blueprint/Moved.tsx';
import { pageName, settingsPagesFor, type SettingsPage } from './parts.ts';
import { BP_LEDE, LEDE, type BlueprintPage } from './ledes.ts';
import { isRulesPage, RulesPage } from './RulesPage.tsx';
import { TaxPage } from './TaxPage.tsx';
import { TimePage } from './TimePage.tsx';
import { TemplatesPage } from './TemplatesPage.tsx';
import { FormsPage } from './FormsPage.tsx';
import { JobTypesPage } from './JobTypesPage.tsx';
import { MembershipPlansPage } from './MembershipPlansPage.tsx';
import { DevicesPage } from './DevicesPage.tsx';
import { CallsPage } from './CallsPage.tsx';
import { CUSTOMER_TEXT_KEYS } from './customer-text-model.ts';
import {
  GIVEN_BEFORE,
  holdersOf,
  invitedWords,
  offersSignOut,
  matrixGroups,
  RIVERSTONE_RULE_KEYS,
  RIVERSTONE_RULES,
  rolesToAdd,
  rulesCardLine,
  ruleSection,
  settingsRights,
  signInWords,
  whatTheyCanDo,
  type MatrixPermission,
  type Rights,
  type SignInState,
} from './settings-model.ts';
import { Loading } from '../shared/Loading.tsx';
import '../review/review.css';
import '../history/history.css';
import { GivenPermissions, LimitedRoles, NamedExceptions } from './Grants.tsx';
import { AgentSettingsPage } from '../agents/Settings.tsx';
import { PlansPage } from '../memberships/Memberships.tsx';
import { Connections } from '../books/Connections.tsx';
import { useToast } from '../books/ui.tsx';
import { LicencesExpiring } from './Licences.tsx';
import './settings.css';
import { scrollFocus } from '../shared/scrollFocus.ts';
import { NumberingCard } from './Numbering.tsx';
import { RuleRows, RuleSections, SectionRules, useRuleData } from './RuleSection.tsx';
import { EditProfile, OrgTab, PricebookTab, TaxTab, TemplatesTab } from './Setup.tsx';
import { DevicesTab } from './Devices.tsx';
import { FormsTab } from './Forms.tsx';
import { CallReasonsTab } from './CallReasons.tsx';
import { StaffHoursSheet, TimeTab } from './Hours.tsx';
import { hoursRights } from './hours-model.ts';
import { CampaignsTab } from './Campaigns.tsx';
import { OptionTemplatesCard } from './OptionTemplates.tsx';
import { PricebookArtLine } from './PricebookArt.tsx';
import { PicturesToApprove } from './PicturesToApprove.tsx';
import { PricebookPlace } from './PricebookPlace.tsx';
import { setupRights } from './setup-model.ts';
import { VIEW_PERMS as MIGRATION_VIEW_PERMS } from '../migration/model.ts';
import { stepUpWith } from '../shared/stepUp.ts';
import { useStepUp } from '../shared/StepUpAsk.tsx';
import { CELL_WORDS, gridCell, gridFor } from './roles-grid-model.ts';
import '@flowology/ui-web/base.css';
import '../blueprint/setup.css';
import '../blueprint/setup-settings.css';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

type Page = SettingsPage;
interface Unit {
  id: string;
  name: string;
}
export function SettingsScreen({
  nav,
  timeZone,
  open,
  onPart,
}: {
  nav: NavProps;
  timeZone: string;
  /** A part to show, asked for from outside (its link, #/settings/people); `at` makes each ask a new one. */
  open?: { part: SettingsPage; at: number };
  /** The part shown, each time it changes: the address follows it. */
  onPart?: (part: SettingsPage) => void;
}) {
  const bp = useDesign() === 'blueprint';
  const [asked, setPage] = useState<Page>(open?.part ?? 'rules');
  useEffect(() => {
    if (open) setPage(open.part);
  }, [open?.part, open?.at]);
  // what opens and what's editable, by the permissions the person holds (QA-681)
  const held = nav.permissions ?? [];
  const rights = settingsRights(held);
  const setup = setupRights(held);
  const hours = hoursRights(held);
  // While the company mirrors ServiceTitan its price book, departments, job types, tax zones and call
  // reasons are ServiceTitan's (st-mirror §2): their tabs read, with the line (BL-088)
  const mirroring = useMirror();
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  // the parts: tabs in Riverstone, the Setup rail in Blueprint (settings/parts.ts)
  const tabs = settingsPagesFor(held, bp).map((p) => [p.page, pageName(p, bp)!] as const);
  // a part they don't open (a link someone sent) shows the first
  const page: Page = tabs.some(([k]) => k === asked) ? asked : (tabs[0]?.[0] ?? 'rules');
  const said = useRef(onPart);
  said.current = onPart;
  useEffect(() => said.current?.(page), [page]);
  // a way to the move off ServiceTitan, for those who see it (BL-103)
  const toMigration = holdsAny(held, MIGRATION_VIEW_PERMS);
  // Blueprint: each page's rules beside the lists they govern (RULE_SECTIONS), before or after them
  const before = bp && <RuleSections key={`${page}-top`} page={page} top rights={rights} />;
  const after = bp && page !== 'pricebook' && (
    <RuleSections
      key={page}
      page={page}
      rights={rights}
      inside={{
        'invoices.signed': <NumberingCard canEdit={rights.edit} bare />,
      }}
    />
  );
  const [toast, setToast] = useToast();
  // Blueprint's price book is the approved page (ui-web's PricebookView), its own title block under the
  // frame's crumb; its other sections sit under the list
  const crumb = usePageCrumb(nav);
  const pricebookRules = bp && (
    <>
      <OptionTemplatesCard canEdit={setup.pricebook} />
      <RuleSections
        page="pricebook"
        rights={rights}
        inside={{
          // the book's pictures under their switches, told again each time pictures go on or off;
          // then the new ones waiting for the owner or a manager to approve them (BL-122)
          'pricebook.pictures': (rows) => (
            <>
              <PricebookArtLine
                canEdit={setup.pricebook}
                enabled={rows.find((r) => r.key === 'pricebook.art.enabled')?.value === true}
              />
              <PicturesToApprove canEdit={setup.pricebook} />
            </>
          ),
        }}
      />
    </>
  );
  // Blueprint's pages that are rules alone are on the SettingsPage pattern (RulesPage.tsx, BL-189)
  if (bp && isRulesPage(page)) return <RulesPage nav={nav} page={page} />;
  if (bp && page === 'tax') return <TaxPage nav={nav} timeZone={timeZone} />;
  if (bp && page === 'time') return <TimePage nav={nav} timeZone={timeZone} />;
  if (bp && page === 'templates') return <TemplatesPage nav={nav} />;
  if (bp && page === 'org') return <JobTypesPage nav={nav} />;
  if (bp && page === 'forms') return <FormsPage nav={nav} />;
  if (bp && page === 'plans') return <MembershipPlansPage nav={nav} />;
  if (bp && page === 'devices') return <DevicesPage nav={nav} timeZone={timeZone} />;
  if (bp && page === 'calls') return <CallsPage nav={nav} />;
  if (bp && page === 'pricebook')
    return (
      // ui-web's page (PricebookView), drawn as Equipment's is: the sheet pads the page, not .page
      <div className="app">
        <main>
          <PageBanners nav={nav} />
          <PricebookPlace
            held={held}
            mirroring={mirroring}
            toCatalog={holdsAny(held, INVENTORY_PERMS)}
            timeZone={timeZone}
            crumb={crumb?.slice(0, 1)}
          >
            {mirroring && setup.pricebook && (
              // Blueprint names no import source in Setup's copy (Amir, 25 Oct)
              <p className="muted small">Imported items and categories: {MIRROR_LINE}</p>
            )}
            {pricebookRules}
          </PricebookPlace>
        </main>
      </div>
    );
  const parts = (
    <>
      {before}
      {page === 'rules' && <Rules rights={rights} />}
      {page === 'roles' && <Roles owner={rights.owner} />}
      {/* Blueprint: the exceptions to the roles, for named people, beside the roles (the setup audit) */}
      {page === 'roles' && bp && rights.owner && <NamedExceptions timeZone={timeZone} />}
      {page === 'agents' && <AgentSettingsPage held={held} />}
      {/* in .books, so Books' Blueprint styles hold (books/blueprint.css) */}
      {page === 'connections' && (
        <div className="books">
          <Connections toast={setToast} timeZone={timeZone} />
        </div>
      )}
      {page === 'plans' && <PlansPage held={held} timeZone={timeZone} />}
      {page === 'people' && (
        <People
          owner={rights.owner}
          viewerStaffId={nav.staffId ?? null}
          canInvite={setup.staff}
          canEditProfile={setup.staff}
          hours={hours}
          timeZone={timeZone}
        />
      )}
      {page === 'time' && <TimeTab rights={hours} timeZone={timeZone} />}
      {page === 'devices' && rights.edit && (
        <DevicesTab
          timeZone={timeZone}
          owner={held.includes('sync.sealed.discard')}
          onReview={() => nav.onView('review')}
        />
      )}
      {page === 'pricebook' && (
        <>
          {mirroring && setup.pricebook && (
            <p className="muted small">
              {/* Blueprint names no import source in Setup's copy (Amir, 25 Oct) */}
              {bp ? 'Imported items and categories' : 'ServiceTitan’s items and categories'}: {MIRROR_LINE}
            </p>
          )}
          <PricebookTab
            canEdit={setup.pricebook}
            mirroring={mirroring}
            toCatalog={holdsAny(held, INVENTORY_PERMS)}
            timeZone={timeZone}
          >
            {/* Blueprint: the price limits under the option templates, then the pictures with their
                switches (BL-109, BL-141), the spreadsheet last */}
            {pricebookRules}
          </PricebookTab>
          {!bp && <OptionTemplatesCard canEdit={setup.pricebook} />}
        </>
      )}
      {mirroring && page === 'org' && setup.org && (
        <p className="muted small">
          Departments’ and job types’ names: {MIRROR_LINE} Their defaults, a department’s customer text and
          the licences stay yours.
        </p>
      )}
      {mirroring && page === 'tax' && setup.org && <p className="muted small">{MIRROR_LINE}</p>}
      {page === 'org' && <OrgTab canEdit={setup.org} mirroring={mirroring} />}
      {page === 'tax' && <TaxTab canEdit={setup.org && !mirroring} today={today} />}
      {page === 'templates' && <TemplatesTab canEdit={setup.templates} />}
      {page === 'forms' && setup.org && <FormsTab />}
      {page === 'calls' && mirroring && rights.edit && <p className="muted small">{MIRROR_LINE}</p>}
      {page === 'calls' && <CallReasonsTab canEdit={rights.edit && !mirroring} />}
      {page === 'campaigns' && setup.seesMarketing && (
        <CampaignsTab canEdit={setup.marketing}>
          {/* Blueprint: the ads' monthly cap, a part of Ad spend like its linked campaigns */}
          {bp && (
            <div className="bp-part" role="group" aria-label="Monthly cap">
              <h3>Monthly cap</h3>
              <p className="muted">{ruleSection('campaigns.spend')?.line}</p>
              <SectionRules id="campaigns.spend" rights={rights} />
            </div>
          )}
        </CampaignsTab>
      )}
      {after}
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
    </>
  );
  return (
    <div className={bp ? 'app bp-setup flw' : 'app'}>
      <Header
        nav={nav}
        title={bp ? (tabs.find(([k]) => k === page)?.[1] ?? 'Settings') : 'Settings'}
        lede={(bp ? BP_LEDE[page] : undefined) ?? LEDE[page as Exclude<Page, BlueprintPage>]}
      />
      <main className="page settings">
        {bp ? (
          // Blueprint: the Setup rail lists the parts (blueprint/places.ts), and Data import is its own
          // page there, so the sheet holds the part alone
          <div className="settings-page">{parts}</div>
        ) : (
          <>
            <div className="settings-nav">
              <div className="tabs" role="tablist" aria-label="Settings">
                {tabs.map(([k, label]) => (
                  <button
                    key={k}
                    role="tab"
                    aria-selected={page === k}
                    className={page === k ? 'on' : ''}
                    onClick={() => setPage(k)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {/* BL-103: the move off ServiceTitan is its own place; from here, a way to it for those who see it */}
              {toMigration && (
                <a className="btn quiet settings-out" href="#/migration">
                  Migration ›
                </a>
              )}
            </div>
            {parts}
          </>
        )}
      </main>
    </div>
  );
}

// ───────── Rules & defaults ─────────

function Rules({ rights }: { rights: Rights }) {
  const data = useRuleData();
  const { rows, error, load } = data;
  if (error) return <p className="error">{error}</p>;
  if (!rows) return <Loading error={null} what="the rules" retry={() => void load()} />;
  // the customer text is edited under Departments & job types (BL-052), not as a raw value here
  const others = rows.filter(
    (r) => !RIVERSTONE_RULE_KEYS.has(r.key) && !CUSTOMER_TEXT_KEYS.has(r.key) && !r.key.endsWith('.*'),
  );
  return (
    <>
      {RIVERSTONE_RULES.map((g) => (
        <section key={g.title} className="sheet card" aria-label={g.title}>
          <h2>{g.title}</h2>
          {rulesCardLine(g.rules, rights, rows) && (
            <p className="muted small read-only">{rulesCardLine(g.rules, rights, rows)}</p>
          )}
          <RuleRows rules={g.rules} fixed={g.fixed} data={data} rights={rights} />
        </section>
      ))}
      <NumberingCard canEdit={rights.edit} />
      {others.length > 0 && (
        <details className="sheet card">
          <summary>Other settings ({others.length})</summary>
          <ul className="rules">
            {others.map((r) => (
              <li key={r.key} className="rule">
                <div className="what">
                  <code>{r.key}</code>
                  <p className="muted small">{r.description}</p>
                </div>
                <code className="small val">{JSON.stringify(r.value)}</code>
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}

// ───────── Roles & permissions ─────────

/** People as the role editor needs them (people.list): who holds which roles. */
interface Holder {
  staffId: string;
  displayName: string;
  roleKeys: string[] | null;
}

function Roles({ owner }: { owner: boolean }) {
  const [data, setData] = useState<{
    roles: { key: string; name: string; system: boolean; permissions: string[] }[];
    permissions: MatrixPermission[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cloning, setCloning] = useState<{ key: string; name: string } | null>(null);
  const [creating, setCreating] = useState(false);
  // who holds each role (BL-137); null until people.list answers, or when it can't be read here
  const [people, setPeople] = useState<Holder[] | null>(null);
  // Blueprint: who may do what at a glance first (PermissionGrid), every permission one tap away
  const bp = useDesign() === 'blueprint';
  const [full, setFull] = useState(false);
  // changing a role asks the owner's fresh second factor (QA-685)
  const { run, ask } = useStepUp();
  const load = () =>
    client
      .call<NonNullable<typeof data>>('roles.list', {})
      .then(setData)
      .catch((e: unknown) => setError(message(e)));
  useEffect(() => {
    void load();
    client
      .call<{ people: Holder[] }>('people.list', {})
      .then((r) => setPeople(r.people))
      .catch(() => setPeople(null));
  }, []);
  /** One of the company's own roles: the owner turns a permission on or off (roles.update). */
  const toggle = async (role: { key: string; permissions: string[] }, permission: string) => {
    const on = role.permissions.includes(permission);
    try {
      await run(() =>
        client.call('roles.update', {
          key: role.key,
          permissions: on
            ? role.permissions.filter((x) => x !== permission)
            : [...role.permissions, permission],
        }),
      );
      await load();
    } catch (e) {
      setError(message(e));
    }
  };
  if (error) return <p className="error">{error}</p>;
  if (!data) return <Loading error={null} what="the roles" kind="table" cols={['Permission']} />;
  const groups = matrixGroups(data.permissions);
  const system = data.permissions.filter((p) => p.systemOnly);
  // Who has each role (BL-137), under the grid or the matrix
  const holders = people && (
    <section className="sheet card role-holders" aria-label="Who has each role">
      <h2>Who has each role</h2>
      <p className="muted small">
        A person may hold several roles and may do what any of them allows. Change someone’s roles under
        People.
      </p>
      <ul className="rows">
        {data.roles.map((r) => {
          const names = holdersOf(r.key, people);
          return (
            <li key={r.key} className="role-holder">
              <b>{r.name}</b>
              <span className="holders">
                {names.length ? (
                  names.map((n) => (
                    <span key={n} className="chip say">
                      {n}
                    </span>
                  ))
                ) : (
                  <span className="muted small">Nobody yet</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
  const newRole = owner && (
    <button type="button" className="btn soft" onClick={() => setCreating(true)}>
      New role
    </button>
  );
  const createDialog = creating && (
    <NewRoleDialog
      onClose={() => setCreating(false)}
      onDone={async () => {
        setCreating(false);
        setFull(true); // a new role starts empty: its permissions are ticked in the full matrix
        await load();
      }}
    />
  );
  if (bp && !full)
    return (
      <>
        <section className="sheet roles-grid" aria-label="Roles">
          <h2>Roles</h2>
          <p className="muted">
            What each role may do, at a glance. A person may hold several roles and may do what any of them
            allows. What only the owner gives (costs, margins, payroll) goes in roles only the owner makes and
            gives. A role of your own is changed from every permission.
          </p>
          <div className="tablewrap">
            <table className="t">
              <thead>
                <tr>
                  <th scope="col">What</th>
                  {data.roles.map((r) => (
                    <th key={r.key} scope="col" className="c">
                      {r.name}
                    </th>
                  ))}
                </tr>
              </thead>
              {gridFor(data.permissions).map((g) => (
                <tbody key={g.group}>
                  <tr className="grouphead">
                    <th scope="rowgroup" colSpan={data.roles.length + 1}>
                      {g.group}
                    </th>
                  </tr>
                  {g.rows.map((row) => (
                    <tr key={row.what}>
                      <th scope="row">{row.what}</th>
                      {data.roles.map((r) => {
                        const cell = gridCell(r, row.keys, data.permissions);
                        return (
                          <td
                            key={r.key}
                            className={`c grid-${cell}`}
                            title={CELL_WORDS[cell].says}
                            aria-label={`${r.name}: ${CELL_WORDS[cell].says.toLowerCase()}`}
                          >
                            {CELL_WORDS[cell].mark}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
          <ul className="roles-key">
            {(['yes', 'no', 'some', 'named'] as const).map((c) => (
              <li key={c}>
                <b className={`grid-${c}`}>{CELL_WORDS[c].mark}</b> {CELL_WORDS[c].says}
              </li>
            ))}
          </ul>
          <button type="button" className="bp-more" onClick={() => setFull(true)}>
            Show every permission ({groups.reduce((n, g) => n + g.permissions.length, 0)})
          </button>
          {newRole}
        </section>
        {holders}
        {createDialog}
      </>
    );
  const matrix = (
    <>
      <div className="row between">
        <p className="muted small lede">
          System roles stay as shipped. Owner only permissions go only in roles the owner makes, and the owner
          gives those roles. Point at a – to see why it isn’t there.
        </p>
        {newRole}
      </div>
      <div className="tablewrap sheet matrix" ref={scrollFocus}>
        <table className="t">
          <thead>
            <tr>
              <th scope="col">Permission</th>
              {data.roles.map((r) => (
                // Named by the role alone, so a cell's column reads "Technician", not "Technician Clone the…"
                <th key={r.key} scope="col" className="c" aria-label={r.name}>
                  {r.name}
                  {owner && r.key !== 'owner' && (
                    <div>
                      <button
                        type="button"
                        className="btn quiet sm"
                        aria-label={`Clone the ${r.name} role`}
                        onClick={() => setCloning({ key: r.key, name: r.name })}
                      >
                        Clone
                      </button>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          {groups.map((g) => (
            <tbody key={g.title}>
              <tr className="grouphead">
                <th scope="rowgroup" colSpan={data.roles.length + 1}>
                  {g.title}
                </th>
              </tr>
              {g.permissions.map((p) => (
                <tr key={p.key}>
                  <th scope="row">
                    {p.description}
                    {p.ownerOnly && <span className="chip say warn">Owner only</span>}
                    <div className="act">{p.key}</div>
                  </th>
                  {data.roles.map((r) => {
                    const on = r.permissions.includes(p.key);
                    // what stays with the Owner role goes in no other (BL-137); owner-only ones may
                    const kept = p.ownerOnly && p.grantable === false;
                    const why = on
                      ? undefined
                      : kept
                        ? 'It stays with the Owner role, in no other.'
                        : r.system
                          ? `The ${r.name} role is a system role and stays as shipped.`
                          : undefined;
                    // The company's own roles are the owner's to change, a click at a time.
                    const editable = owner && !r.system && !kept && !p.systemOnly;
                    return (
                      <td
                        key={r.key}
                        className={`c${why ? ' locked' : ''}`}
                        title={why}
                        // An editable cell's button carries its name and pressed state; a fixed one says yes or no.
                        aria-label={editable ? undefined : `${r.name}: ${on ? 'yes' : 'no'}`}
                      >
                        {editable ? (
                          <button
                            type="button"
                            className="btn quiet sm"
                            aria-pressed={on}
                            aria-label={`${r.name}: ${p.description}`}
                            onClick={() => void toggle(r, p.key)}
                          >
                            {on ? '✓' : '–'}
                          </button>
                        ) : on ? (
                          '✓'
                        ) : (
                          '–'
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>
      <p className="muted small">
        Held by no role, the owner’s included (system handlers, the worker and customer links only):{' '}
        {system.map((p) => p.key).join(', ')}.
      </p>
      {cloning && (
        <CloneDialog
          from={cloning}
          onClose={() => setCloning(null)}
          onDone={async () => {
            setCloning(null);
            await load();
          }}
        />
      )}
      {holders}
      {createDialog}
      {ask}
    </>
  );
  return bp ? (
    <>
      <button type="button" className="bp-more roles-back" onClick={() => setFull(false)}>
        Back to who may do what, at a glance
      </button>
      {matrix}
    </>
  ) : (
    matrix
  );
}

/** Clone a role: an editable copy with its own name (roles.clone, the owner's). */
function CloneDialog({
  from,
  onClose,
  onDone,
}: {
  from: { key: string; name: string };
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const ref = useModal();
  const [name, setName] = useState(`${from.name} (copy)`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { run, ask } = useStepUp();
  return (
    <>
      <dialog ref={ref} className="sheet ask" aria-labelledby="clone-title" onClose={onClose}>
        <form
          method="dialog"
          onSubmit={(e) => {
            e.preventDefault();
            setBusy(true);
            run(() => client.call('roles.clone', { fromKey: from.key, name: name.trim() }))
              .then(onDone)
              .catch((err: unknown) => {
                setError(message(err));
                setBusy(false);
              });
          }}
        >
          <h3 id="clone-title" className="sheet-title">
            Clone the {from.name} role
          </h3>
          <p className="muted small">
            The copy starts with the same permissions and is yours to change; {from.name} stays as it is.
          </p>
          <label>
            Name
            <input className="field" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <div className="row end">
            <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={busy || !name.trim()}>
              {busy ? 'Cloning…' : 'Clone'}
            </button>
          </div>
        </form>
      </dialog>
      {ask}
    </>
  );
}

/** A new role from nothing (roles.create, the owner's): a name now, its permissions in the matrix. */
function NewRoleDialog({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<void> }) {
  const ref = useModal();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { run, ask } = useStepUp();
  return (
    <>
      <dialog ref={ref} className="sheet ask" aria-labelledby="new-role-title" onClose={onClose}>
        <form
          method="dialog"
          onSubmit={(e) => {
            e.preventDefault();
            setBusy(true);
            run(() => client.call('roles.create', { name: name.trim(), permissions: [] }))
              .then(onDone)
              .catch((err: unknown) => {
                setError(message(err));
                setBusy(false);
              });
          }}
        >
          <h3 id="new-role-title" className="sheet-title">
            New role
          </h3>
          <p className="muted small">
            It starts with nothing: tick what it allows in the full list. Give it to people beside their other
            roles, such as a Margins role beside Customer service.
          </p>
          <label>
            Name
            <input className="field" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <div className="row end">
            <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={busy || !name.trim()}>
              {busy ? 'Making…' : 'Make the role'}
            </button>
          </div>
        </form>
      </dialog>
      {ask}
    </>
  );
}

// ───────── People ─────────

interface Person {
  staffId: string;
  displayName: string;
  email: string | null;
  isTechnician: boolean;
  status: string;
  roleKeys: string[] | null;
  scopes: { roleKey: string; businessUnitIds: string[]; locationIds: string[] }[];
  grants: { permission: string; expiresAt: string | null }[] | null;
  /** The email they sign in with; null when they don't. */
  signInEmail: string | null;
  signIn: SignInState | null;
}

function People({
  owner,
  viewerStaffId,
  canInvite,
  canEditProfile,
  hours,
  timeZone,
}: {
  owner: boolean;
  /** The signed-in person's own staff record: never offered a sign-out on their own row (QA-543). */
  viewerStaffId: string | null;
  canInvite: boolean;
  /** staff.edit: name, phone, colour, skills, technician, active (staff.updateProfile) */
  canEditProfile: boolean;
  /** Working hours, time off (staff.hours.manage) and the codes (BL-014 phase 2). */
  hours: { manage: boolean; codes: boolean };
  timeZone: string;
}) {
  const [profileFor, setProfileFor] = useState<string | null>(null);
  const [hoursFor, setHoursFor] = useState<Person | null>(null);
  // taking back a permission, and an invitation that hands out access, ask a fresh second factor (QA-685)
  const { run, ask } = useStepUp();
  // Blueprint: one action on a person's row, Change, which opens their actions (one at a time)
  const bp = useDesign() === 'blueprint';
  const [opened, setOpened] = useState<string | null>(null);
  const [people, setPeople] = useState<Person[] | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  // the roles and every permission (roles.list): a person's roles add up to what they may do (BL-137)
  const [roles, setRoles] = useState<{ key: string; name: string; permissions: string[] }[]>([]);
  const [perms, setPerms] = useState<MatrixPermission[]>([]);
  const roleNames = Object.fromEntries(roles.map((x) => [x.key, x.name]));
  const [limit, setLimit] = useState<{ person: Person; roleKey: string } | null>(null);
  // the roles dialog, with the change asked for when the server wants a step-up or an "I understand"
  const [rolesFor, setRolesFor] = useState<{
    person: Person;
    start?: string[];
    ask?: 'step_up' | 'field';
  } | null>(null);
  const [inviting, setInviting] = useState(false);
  const [switching, setSwitching] = useState<{
    person: { displayName: string; staffId?: string; membershipId?: string };
    mode: SwitchMode;
  } | null>(null);
  const [loose, setLoose] = useState<LooseInvitation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);
  const load = useCallback(async () => {
    try {
      const r = await client.call<{ people: Person[]; invitations?: LooseInvitation[] }>('people.list', {});
      setPeople(r.people);
      setLoose(r.invitations ?? []);
      setError(null);
    } catch (e) {
      setError(message(e));
    }
  }, []);
  useEffect(() => {
    void load();
    client
      .call<{ businessUnits: Unit[] }>('businessUnits.list', {})
      .then((r) => setUnits(r.businessUnits))
      .catch(() => undefined);
    client
      .call<{
        roles: { key: string; name: string; permissions: string[] }[];
        permissions: MatrixPermission[];
      }>('roles.list', {})
      .then((r) => {
        setRoles(r.roles);
        setPerms(r.permissions);
      })
      .catch(() => undefined);
  }, [load]);
  const unitName = (id: string) => units.find((u) => u.id === id)?.name ?? 'a department';
  const ownerOnly = perms.filter((p) => p.ownerOnly);
  /** Give or take one role (people.setRoles): straight away, or through the dialog when it asks first. */
  const changeRoles = async (person: Person, keys: string[], said: string) => {
    try {
      await client.call('people.setRoles', { staffId: person.staffId, roleKeys: keys });
      await load();
      setToast(said);
    } catch (e) {
      const reason =
        e instanceof RpcError ? (e.detail as { reason?: string } | undefined)?.reason : undefined;
      if (reason === 'step_up_required') setRolesFor({ person, start: keys, ask: 'step_up' });
      else if (reason === 'field_role_confirm') setRolesFor({ person, start: keys, ask: 'field' });
      else setError(message(e));
    }
  };
  const when = new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium' });
  if (error) return <p className="error">{error}</p>;
  if (!people) return <Loading error={null} what="the people" retry={() => void load()} />;
  return (
    <>
      <div className="row between">
        <p className="muted small lede">
          A person may hold several roles and may do what any of them allows. Only the owner gives roles or
          limits one to some departments.
        </p>
        {canInvite && (
          <button className="btn primary" onClick={() => setInviting(true)}>
            Invite someone
          </button>
        )}
      </div>
      {/* the owner's overview of owner-only permissions given to people (gap #18) */}
      {owner && !bp && <GivenPermissions people={people} permissions={ownerOnly} timeZone={timeZone} />}
      {/* and the roles limited to some places (roles.listScopes) */}
      {owner && !bp && <LimitedRoles people={people} roleNames={roleNames} unitName={unitName} />}
      {/* Blueprint: both lists are Who may do what's, beside the roles (the setup audit) */}
      {owner && bp && (
        <Moved
          what="The permissions given to people and the limited roles"
          to="Who may do what"
          href="#/settings/roles"
        />
      )}
      {/* the licences expired or expiring soon, for the people who keep the profiles (BL-027) */}
      {canEditProfile && <LicencesExpiring timeZone={timeZone} people={people} />}
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
      {hoursFor && (
        <StaffHoursSheet
          person={hoursFor}
          rights={hours}
          timeZone={timeZone}
          onClose={() => setHoursFor(null)}
        />
      )}
      {profileFor && (
        <EditProfile
          staffId={profileFor}
          timeZone={timeZone}
          onSaved={() => {
            setProfileFor(null);
            void load();
          }}
          onClose={() => setProfileFor(null)}
        />
      )}
      <ul className="rows people sheet">
        {people.map((p) => {
          const acts = !bp || opened === p.staffId;
          return (
            <li key={p.staffId} className={bp && acts ? 'person open' : 'person'}>
              <div className="who">
                <b>{p.displayName}</b>
                <span className="muted small">
                  {/* the email they sign in with, before the staff record's (QA-353) */}
                  {p.signInEmail ?? p.email ?? 'No email'}
                  {p.isTechnician ? ' · technician' : ''}
                  {p.status !== 'active' ? ` · ${p.status}` : ''}
                </span>
                {signInWords(p.signIn, timeZone) && (
                  <span className="small signin-state">{signInWords(p.signIn, timeZone)}</span>
                )}
                {acts && canInvite && p.signIn?.status === 'invited' && p.signIn.invitedAt && (
                  <span className="invite-acts">
                    <button
                      className="btn quiet small"
                      onClick={() =>
                        void run(() =>
                          client.call<{ emailed: boolean; signInAt: string | null }>('people.resendInvite', {
                            staffId: p.staffId,
                          }),
                        )
                          .then(async (r) => {
                            await load();
                            setToast(invitedWords(r, p.displayName, p.signInEmail ?? 'their email'));
                          })
                          .catch((e: unknown) => setError(message(e)))
                      }
                    >
                      Send again
                    </button>
                    <button
                      className="btn quiet small"
                      onClick={() =>
                        void client
                          .call('people.cancelInvite', { staffId: p.staffId })
                          .then(load)
                          .then(() => setToast(`${p.displayName}’s invitation is cancelled.`))
                          .catch((e: unknown) => setError(message(e)))
                      }
                    >
                      Cancel invitation
                    </button>
                  </span>
                )}
              </div>
              <div className="roles">
                {p.roleKeys === null ? (
                  <span className="muted small">Doesn’t sign in</span>
                ) : (
                  p.roleKeys.map((k) => {
                    const s = p.scopes.find((x) => x.roleKey === k);
                    const name = roleNames[k] ?? k;
                    return (
                      <span key={k} className="rolechip">
                        <span className="chip say brand">{name}</span>
                        <span className="small">
                          {s
                            ? `only ${s.businessUnitIds.map(unitName).join(', ') || 'some locations'}`
                            : 'company-wide'}
                        </span>
                        {acts && owner && k !== 'owner' && (
                          <button
                            className="btn quiet small"
                            onClick={() => setLimit({ person: p, roleKey: k })}
                          >
                            {s ? 'Change' : 'Limit'}
                          </button>
                        )}
                        {/* a role taken away takes only what none of their other roles gives (BL-137) */}
                        {acts && owner && p.roleKeys!.length > 1 && (
                          <button
                            className="btn quiet small"
                            aria-label={`Take ${name} from ${p.displayName}`}
                            onClick={() =>
                              void changeRoles(
                                p,
                                p.roleKeys!.filter((x) => x !== k),
                                `${name} is taken from ${p.displayName}.`,
                              )
                            }
                          >
                            ×
                          </button>
                        )}
                      </span>
                    );
                  })
                )}
                {acts && owner && p.roleKeys !== null && rolesToAdd(p.roleKeys, roles).length > 0 && (
                  <label className="add-role">
                    <span className="sr">Add a role for {p.displayName}</span>
                    <select
                      className="field"
                      value=""
                      onChange={(e) => {
                        const k = e.target.value;
                        if (k)
                          void changeRoles(
                            p,
                            [...p.roleKeys!, k],
                            `${p.displayName} has ${roleNames[k] ?? k} now, beside their other roles.`,
                          );
                      }}
                    >
                      <option value="">Add a role…</option>
                      {rolesToAdd(p.roleKeys, roles).map((r) => (
                        <option key={r.key} value={r.key}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              {bp && (
                <div className="give">
                  <button
                    className="btn"
                    aria-expanded={acts}
                    aria-label={`${acts ? 'Close' : 'Change'} ${p.displayName}’s settings`}
                    onClick={() => setOpened(acts ? null : p.staffId)}
                  >
                    {acts ? 'Done' : 'Change'}
                  </button>
                </div>
              )}
              {/* their hours and time off, for everyone who sees People; changed by those who schedule (BL-014) */}
              {acts && (
                <div className="give">
                  <button
                    className="btn soft"
                    aria-label={`${p.displayName}’s ${bp ? 'working hours' : 'hours and time off'}`}
                    onClick={() => setHoursFor(p)}
                  >
                    {/* Blueprint: time off is Schedule › Team › Time off's (the setup audit) */}
                    {bp ? 'Working hours' : 'Hours & time off'}
                  </button>
                </div>
              )}
              {/* the person's own actions, at the row's end: their profile first, then (owner) their access */}
              {acts && canEditProfile && !(owner && p.roleKeys !== null) && (
                <div className="give">
                  <button
                    className="btn soft"
                    aria-label={`Edit ${p.displayName}’s profile`}
                    onClick={() => setProfileFor(p.staffId)}
                  >
                    Edit profile
                  </button>
                </div>
              )}
              {acts && offersSignOut({ owner, managesPeople: canInvite, staffId: viewerStaffId }, p) && (
                <div className="give">
                  <SignOutEverywhere
                    staffId={p.staffId}
                    name={p.displayName}
                    onDone={(t) => {
                      setToast(t);
                      void load();
                    }}
                  />
                </div>
              )}
              {acts && owner && p.roleKeys !== null && (
                <div className="give">
                  {canEditProfile && (
                    <button
                      className="btn soft"
                      aria-label={`Edit ${p.displayName}’s profile`}
                      onClick={() => setProfileFor(p.staffId)}
                    >
                      Edit profile
                    </button>
                  )}
                  <button className="btn soft" onClick={() => setRolesFor({ person: p })}>
                    Roles…
                  </button>
                  {p.signIn?.status === 'active' && (
                    <button className="btn danger" onClick={() => setSwitching({ person: p, mode: 'off' })}>
                      Turn off sign-in…
                    </button>
                  )}
                  {p.signIn?.status === 'disabled' && (
                    <button className="btn soft" onClick={() => setSwitching({ person: p, mode: 'on' })}>
                      Turn sign-in back on…
                    </button>
                  )}
                  {p.signIn?.status === 'invited' && p.signIn.ownerConfirmPending && (
                    <button className="btn soft" onClick={() => setSwitching({ person: p, mode: 'confirm' })}>
                      Confirm as owner…
                    </button>
                  )}
                </div>
              )}
              {/* what their roles add up to (BL-137): open on their row */}
              {acts && p.roleKeys !== null && perms.length > 0 && (
                <CanDoList person={p} roles={roles} perms={perms} />
              )}
              {/* grants from before roles held owner-only permissions: they run to their date */}
              {p.grants !== null && p.grants.length > 0 && (
                <div className="grants">
                  {p.grants.map((g) => (
                    <span key={g.permission} className="rolechip">
                      <span className="chip say warn">{GIVEN_BEFORE}</span>
                      <span className="small">
                        <b>{ownerOnly.find((o) => o.key === g.permission)?.description ?? g.permission}</b>
                      </span>
                      <span className="small">
                        {g.expiresAt ? `until ${when.format(new Date(g.expiresAt))}` : 'no end date'}
                      </span>
                      <button
                        className="btn quiet small"
                        onClick={() =>
                          void run(() =>
                            client.call('permissions.revoke', {
                              staffId: p.staffId,
                              permission: g.permission,
                            }),
                          )
                            .then(load)
                            .then(() => setToast(`Taken back from ${p.displayName}.`))
                            .catch((e: unknown) => setError(message(e)))
                        }
                      >
                        Take back
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {limit && (
        <LimitDialog
          person={limit.person}
          roleKey={limit.roleKey}
          roleName={roleNames[limit.roleKey] ?? limit.roleKey}
          units={units}
          onClose={() => setLimit(null)}
          onDone={async (text) => {
            setLimit(null);
            await load();
            setToast(text);
          }}
        />
      )}
      {rolesFor && (
        <RolesDialog
          person={rolesFor.person}
          {...(rolesFor.start ? { start: rolesFor.start } : {})}
          {...(rolesFor.ask ? { asked: rolesFor.ask } : {})}
          roleNames={roleNames}
          onClose={() => setRolesFor(null)}
          onDone={async (text) => {
            setRolesFor(null);
            await load();
            setToast(text);
          }}
        />
      )}
      {loose.length > 0 && (
        <section className="sheet loose-invites" aria-labelledby="loose-h">
          <h3 id="loose-h">Invitations with no staff record</h3>
          <p className="muted small">
            Made without Settings. Every owner was told of each. Confirm one you expect, or cancel it.
          </p>
          <ul className="rows">
            {loose.map((l) => (
              <li key={l.membershipId} className="person">
                <div className="who">
                  <b>{l.name}</b>
                  <span className="muted small">
                    {l.email} · {l.roleKeys.map((k) => roleNames[k] ?? k).join(', ')}
                  </span>
                  <span className="small signin-state">
                    {signInWords(
                      {
                        status: 'invited',
                        invitedAt: l.invitedAt,
                        expiresAt: l.expiresAt,
                        lapsed: false,
                        ownerConfirmPending: l.ownerConfirmPending,
                      },
                      timeZone,
                    )}
                  </span>
                </div>
                <div className="give">
                  {owner && l.ownerConfirmPending && (
                    <button
                      className="btn soft"
                      onClick={() =>
                        setSwitching({
                          person: { displayName: l.name, membershipId: l.membershipId },
                          mode: 'confirm',
                        })
                      }
                    >
                      Confirm as owner…
                    </button>
                  )}
                  {canInvite && (
                    <button
                      className="btn quiet small"
                      onClick={() =>
                        void client
                          .call('people.cancelInvite', { membershipId: l.membershipId })
                          .then(load)
                          .then(() => setToast(`${l.name}’s invitation is cancelled.`))
                          .catch((e: unknown) => setError(message(e)))
                      }
                    >
                      Cancel invitation
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
      {switching && (
        <SignInSwitchDialog
          person={switching.person}
          mode={switching.mode}
          onClose={() => setSwitching(null)}
          onDone={async (text) => {
            setSwitching(null);
            await load();
            setToast(text);
          }}
        />
      )}
      {inviting && (
        <InviteDialog
          owner={owner}
          roleNames={roleNames}
          onClose={() => setInviting(false)}
          onInvited={load}
        />
      )}
      {ask}
    </>
  );
}

/** What a person may do (BL-137): the union of their roles, grouped, each with the roles it comes from. */
function CanDoList({
  person,
  roles,
  perms,
}: {
  person: Person;
  roles: readonly { key: string; name: string; permissions: readonly string[] }[];
  perms: readonly MatrixPermission[];
}) {
  const groups = whatTheyCanDo(person.roleKeys ?? [], roles, perms, person.grants ?? []);
  const n = groups.reduce((t, g) => t + g.items.length, 0);
  return (
    <details className="can-do">
      <summary>
        What {person.displayName} can do ({n})
      </summary>
      {groups.map((g) => (
        <section key={g.title} aria-label={g.title}>
          <h4>{g.title}</h4>
          <ul>
            {g.items.map((i) => (
              <li key={i.key} title={i.key}>
                {i.description}
                {i.ownerOnly && <span className="chip say warn">Owner only</span>}
                <span className="muted small"> · {i.from.join(', ')}</span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </details>
  );
}

function useModal() {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  return ref;
}

function LimitDialog({
  person,
  roleKey,
  roleName,
  units,
  onClose,
  onDone,
}: {
  person: Person;
  roleKey: string;
  roleName: string;
  units: Unit[];
  onClose: () => void;
  onDone: (toast: string) => Promise<void>;
}) {
  const ref = useModal();
  const current = person.scopes.find((s) => s.roleKey === roleKey)?.businessUnitIds ?? [];
  const [picked, setPicked] = useState<string[]>(current);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // a role's limits ask the owner's fresh second factor (QA-685)
  const { run, ask } = useStepUp();
  const save = async () => {
    setBusy(true);
    try {
      await run(() =>
        client.call('roles.setScope', {
          staffId: person.staffId,
          roleKey,
          businessUnitIds: picked,
          locationIds: person.scopes.find((s) => s.roleKey === roleKey)?.locationIds ?? [],
        }),
      );
      await onDone(
        picked.length
          ? `${person.displayName}’s ${roleName} role now covers only ${picked.length} department${picked.length === 1 ? '' : 's'}.`
          : `${person.displayName}’s ${roleName} role covers the whole company again.`,
      );
    } catch (e) {
      setError(message(e));
      setBusy(false);
    }
  };
  return (
    <>
      <dialog ref={ref} className="sheet ask" aria-labelledby="limit-title" onClose={onClose}>
        <form
          method="dialog"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <h3 id="limit-title" className="sheet-title">
            Where {person.displayName}’s {roleName} role applies
          </h3>
          <p className="muted">Tick the departments it covers. Untick everything to make it company-wide.</p>
          <fieldset className="checks-list">
            <legend className="sr">Departments</legend>
            {units.map((u) => (
              <label key={u.id} className="check-row">
                <input
                  type="checkbox"
                  checked={picked.includes(u.id)}
                  onChange={(e) =>
                    setPicked(e.target.checked ? [...picked, u.id] : picked.filter((x) => x !== u.id))
                  }
                />
                <span>{u.name}</span>
              </label>
            ))}
          </fieldset>
          {/* What the limit really does (docs/specs/scoped-roles.md §3): job-bound records follow it;
            company-level records stay open; actions that reach every place are refused. */}
          <p className="scope-says" aria-live="polite">
            {picked.length ? (
              <>
                <b>
                  {roleName} for {person.displayName} reaches only{' '}
                  {units
                    .filter((u) => picked.includes(u.id))
                    .map((u) => u.name)
                    .join(' and ')}
                  :
                </b>{' '}
                their jobs, visits and invoices. Customers, the pricebook, staff and settings stay
                company-wide; anything that reaches every place (a new department, company-wide settings, the
                Books’ aging and statements) is refused. What another of their roles allows stays as it is.
              </>
            ) : (
              <>
                <b>Company-wide:</b> the {roleName} role reaches every department.
              </>
            )}
          </p>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <div className="row end">
            <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={busy}>
              Save
            </button>
          </div>
        </form>
      </dialog>
      {ask}
    </>
  );
}

/**
 * Choosing someone's roles (people.setRoles, the owner's). It asks for a fresh two-factor code when the
 * server does, and for an "I understand" when a technician would get owner-only power.
 */
function RolesDialog({
  person,
  start,
  asked,
  roleNames,
  onClose,
  onDone,
}: {
  person: Person;
  /** The roles asked for (a chip taken away, a role added), when the server asked first. */
  start?: string[];
  asked?: 'step_up' | 'field';
  roleNames: Record<string, string>;
  onClose: () => void;
  onDone: (toast: string) => Promise<void>;
}) {
  const ref = useModal();
  const [keys, setKeys] = useState<string[]>(start ?? person.roleKeys ?? []);
  const [ask, setAsk] = useState<'step_up' | 'field' | null>(asked ?? null);
  const [code, setCode] = useState('');
  const [understood, setUnderstood] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const flip = (k: string) => setKeys((ks) => (ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k]));
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (ask === 'step_up') {
        const up = await stepUpWith(code);
        if (!up.ok) {
          setError(up.message);
          setBusy(false);
          return;
        }
      }
      await client.call('people.setRoles', {
        staffId: person.staffId,
        roleKeys: keys,
        ...(ask === 'field' && understood ? { confirm: true } : {}),
      });
      await onDone(`${person.displayName}’s roles are changed.`);
    } catch (e) {
      const reason =
        e instanceof RpcError ? (e.detail as { reason?: string } | undefined)?.reason : undefined;
      if (reason === 'step_up_required') setAsk('step_up');
      else if (reason === 'field_role_confirm') setAsk('field');
      else setError(message(e));
      setBusy(false);
    }
  };
  return (
    <dialog ref={ref} className="sheet ask" aria-labelledby="roles-title" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <h3 id="roles-title" className="sheet-title">
          {person.displayName}’s roles
        </h3>
        <fieldset className="checks">
          <legend className="muted small">
            They sign in with these, and may do what any of them allows. The company always keeps an owner.
          </legend>
          {Object.entries(roleNames).map(([k, name]) => (
            <label key={k} className="check">
              <input type="checkbox" checked={keys.includes(k)} onChange={() => flip(k)} />
              {name}
            </label>
          ))}
        </fieldset>
        {ask === 'step_up' && (
          <label>
            Confirm it’s you: a code from your authenticator app
            <input
              className="field"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </label>
        )}
        {ask === 'field' && (
          <div className="fieldwarn" role="note">
            <p>
              <b>{person.displayName} works in the field.</b> This would put owner-only permissions on{' '}
              {person.displayName}’s iPad, out of the office.
            </p>
            <label className="check">
              <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />
              <span>
                I understand {person.displayName} will have owner-only permissions on the iPad, out of the
                office.
              </span>
            </label>
          </div>
        )}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <div className="row end">
          <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
            Cancel
          </button>
          <button
            type="submit"
            className={ask === 'field' ? 'btn danger' : 'btn primary'}
            disabled={
              busy || !keys.length || (ask === 'field' && !understood) || (ask === 'step_up' && !code.trim())
            }
          >
            {busy ? 'Saving…' : ask === 'field' ? 'Change anyway' : 'Save roles'}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/**
 * Turning someone's sign-in off, with the reason (people.disable), or back on (people.enable): the
 * owner's, with a fresh two-factor code when the server asks.
 */
type SwitchMode = 'off' | 'on' | 'confirm';

/** An open invitation with no staff record (QA-355): made straight through, not by Settings. */
interface LooseInvitation {
  membershipId: string;
  email: string;
  name: string;
  roleKeys: string[];
  invitedAt: string | null;
  expiresAt: string | null;
  ownerConfirmPending: boolean;
}

function SignInSwitchDialog({
  person,
  mode,
  onClose,
  onDone,
}: {
  /** Who: a person on the list, or an invitation with no staff record (by its membership). */
  person: { displayName: string; staffId?: string; membershipId?: string };
  /** Off, back on, or an owner's yes to an invitation as owner (QA-355). */
  mode: SwitchMode;
  onClose: () => void;
  onDone: (toast: string) => Promise<void>;
}) {
  const off = mode === 'off';
  const ref = useModal();
  const [why, setWhy] = useState('');
  const [ask, setAsk] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (ask) {
        const up = await stepUpWith(code);
        if (!up.ok) {
          setError(up.message);
          setBusy(false);
          return;
        }
      }
      if (off) await client.call('people.disable', { staffId: person.staffId, reason: why.trim() });
      else if (mode === 'on') await client.call('people.enable', { staffId: person.staffId });
      else
        await client.call(
          'people.confirmOwnerInvite',
          person.staffId ? { staffId: person.staffId } : { membershipId: person.membershipId },
        );
      await onDone(
        off
          ? `${person.displayName}’s sign-in is off. Their iPad signs out the next time it connects.`
          : mode === 'on'
            ? `${person.displayName} can sign in again.`
            : `${person.displayName} can sign in as an owner now.`,
      );
    } catch (e) {
      const reason =
        e instanceof RpcError ? (e.detail as { reason?: string } | undefined)?.reason : undefined;
      if (reason === 'step_up_required') setAsk(true);
      else setError(message(e));
      setBusy(false);
    }
  };
  return (
    <dialog ref={ref} className="sheet ask" aria-labelledby="switch-title" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <h3 id="switch-title" className="sheet-title">
          {off
            ? `Turn off ${person.displayName}’s sign-in`
            : mode === 'on'
              ? `Turn ${person.displayName}’s sign-in back on`
              : `Confirm ${person.displayName} as an owner`}
        </h3>
        <p className="muted">
          {off
            ? `${person.displayName} is signed out everywhere now. Their iPad signs out the next time it connects, and what it hadn’t sent comes to you to review. Their work and history stay.`
            : mode === 'on'
              ? `${person.displayName} signs in again, as before. Nothing they were signed in to comes back.`
              : `${person.displayName} was invited as an owner without an owner’s yes. Once you confirm, they can sign in and do everything an owner does. Not expected? Cancel the invitation instead.`}
        </p>
        {off && (
          <label>
            Why
            <input className="field" value={why} maxLength={300} onChange={(e) => setWhy(e.target.value)} />
          </label>
        )}
        {ask && (
          <label>
            Confirm it’s you: a code from your authenticator app
            <input
              className="field"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </label>
        )}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <div className="row end">
          <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
            Cancel
          </button>
          <button
            type="submit"
            className={off ? 'btn danger' : 'btn primary'}
            disabled={busy || (off && !why.trim()) || (ask && !code.trim())}
          >
            {busy
              ? 'Saving…'
              : off
                ? 'Turn off sign-in'
                : mode === 'on'
                  ? 'Turn sign-in back on'
                  : 'Confirm as owner'}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/**
 * Inviting someone to sign in (people.invite, QA-322): their email, name and roles. A fresh
 * two-factor code when the server asks (an owner invited), an "I understand" for a technician made
 * owner; then what was sent, or what to tell them when no email goes.
 */
function InviteDialog({
  owner,
  roleNames,
  onClose,
  onInvited,
}: {
  owner: boolean;
  roleNames: Record<string, string>;
  onClose: () => void;
  onInvited: () => Promise<void>;
}) {
  const ref = useModal();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [keys, setKeys] = useState<string[]>([]);
  const [ask, setAsk] = useState<'step_up' | 'field' | null>(null);
  const [code, setCode] = useState('');
  const [understood, setUnderstood] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const flip = (k: string) => setKeys((ks) => (ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k]));
  // Only the owner invites an owner; the server says so too.
  const offered = Object.entries(roleNames).filter(([k]) => owner || k !== 'owner');
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (ask === 'step_up') {
        const up = await stepUpWith(code);
        if (!up.ok) {
          setError(up.message);
          setBusy(false);
          return;
        }
      }
      const r = await client.call<{ emailed: boolean; signInAt: string | null }>('people.invite', {
        email: email.trim(),
        displayName: name.trim(),
        roleKeys: keys,
        ...(ask === 'field' && understood ? { confirm: true } : {}),
      });
      await onInvited();
      setDone(invitedWords(r, name.trim(), email.trim()));
      setBusy(false);
    } catch (e) {
      const reason =
        e instanceof RpcError ? (e.detail as { reason?: string } | undefined)?.reason : undefined;
      if (reason === 'step_up_required') setAsk('step_up');
      else if (reason === 'field_role_confirm') setAsk('field');
      else setError(message(e));
      setBusy(false);
    }
  };
  return (
    <dialog ref={ref} className="sheet ask" aria-labelledby="invite-title" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (done) ref.current?.close();
          else void save();
        }}
      >
        <h3 id="invite-title" className="sheet-title">
          Invite someone to sign in
        </h3>
        {done ? (
          <p role="status">{done}</p>
        ) : (
          <>
            <label>
              Their email
              <span className="muted small hint">
                They sign in with it: Google or Microsoft with this address, or a code sent to it. The
                invitation lasts 14 days.
              </span>
              <input
                className="field"
                type="email"
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label>
              Their name
              <input className="field" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <fieldset className="checks">
              <legend className="muted small">They sign in with these roles.</legend>
              {offered.map(([k, label]) => (
                <label key={k} className="check">
                  <input type="checkbox" checked={keys.includes(k)} onChange={() => flip(k)} />
                  {label}
                </label>
              ))}
            </fieldset>
            {ask === 'step_up' && (
              <label>
                Confirm it’s you: a code from your authenticator app
                <input
                  className="field"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </label>
            )}
            {ask === 'field' && (
              <div className="fieldwarn" role="note">
                <p>
                  <b>{name.trim() || 'They'} works in the field.</b> As owner, they would have owner-only
                  permissions on their iPad, out of the office.
                </p>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={understood}
                    onChange={(e) => setUnderstood(e.target.checked)}
                  />
                  <span>
                    I understand they will have owner-only permissions on the iPad, out of the office.
                  </span>
                </label>
              </div>
            )}
            {error && (
              <p className="error" role="alert">
                {error}
              </p>
            )}
          </>
        )}
        <div className="row end">
          {done ? (
            <button type="submit" className="btn primary">
              Done
            </button>
          ) : (
            <>
              <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
                Cancel
              </button>
              <button
                type="submit"
                className={ask === 'field' ? 'btn danger' : 'btn primary'}
                disabled={
                  busy ||
                  !keys.length ||
                  !email.trim() ||
                  !name.trim() ||
                  (ask === 'field' && !understood) ||
                  (ask === 'step_up' && !code.trim())
                }
              >
                {busy ? 'Inviting…' : ask === 'field' ? 'Invite anyway' : 'Invite'}
              </button>
            </>
          )}
        </div>
      </form>
    </dialog>
  );
}
